import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, ClipboardList, CheckCircle, Clock, User, Plus, Trash2, RefreshCw } from 'lucide-react';
import { supabase } from '../lib/supabase';
import SelectResourceModal from './manage/SelectResourceModal';
import WorkflowStepLibraryModal from './WorkflowStepLibraryModal';

interface DefaultResource {
  id: string;
  first_name: string;
  last_name: string;
  email?: string;
  avatar_url?: string;
}

interface BookingWorkflowStep {
  workflow_step_library_id: string;
  title: string;
  description?: string;
  order_index: number;
  assigned_resources: DefaultResource[];
}

interface ServiceWorkflow {
  id: string;
  name: string;
  description?: string;
}

interface SimpleWorkflowModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSkip: () => void;
  onContinue: (bookingWorkflowSteps: BookingWorkflowStep[]) => void;
  serviceName: string;
  serviceId: string;
  isEditing?: boolean;
  existingWorkflowData?: BookingWorkflowStep[];
}

export default function SimpleWorkflowModal({
  isOpen,
  onClose,
  onSkip,
  onContinue,
  serviceName,
  serviceId,
  isEditing = false,
  existingWorkflowData = []
}: SimpleWorkflowModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [workflow, setWorkflow] = useState<ServiceWorkflow | null>(null);
  const [bookingWorkflowSteps, setBookingWorkflowSteps] = useState<BookingWorkflowStep[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Resource management state
  const [isSelectResourceModalOpen, setIsSelectResourceModalOpen] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState<number | null>(null);

  // Workflow library swap state
  const [isWorkflowLibraryModalOpen, setIsWorkflowLibraryModalOpen] = useState(false);
  const [currentStepToSwap, setCurrentStepToSwap] = useState<BookingWorkflowStep | null>(null);
  const [insertPosition, setInsertPosition] = useState<number | null>(null);

  // Load workflow data ONLY ONCE when modal first opens - NEVER RELOAD
  const [hasLoadedData, setHasLoadedData] = useState(false);
  
  useEffect(() => {
    console.log('⚡ useEffect TRIGGERED');
    console.log('📋 Props: isOpen:', isOpen, 'serviceId:', serviceId, 'hasLoadedData:', hasLoadedData);
    console.log('📋 isEditing:', isEditing, 'existingWorkflowData.length:', existingWorkflowData?.length || 0);
    console.log('📋 Full existingWorkflowData:', JSON.stringify(existingWorkflowData, null, 2));
    
    if (isOpen && serviceId && !hasLoadedData) {
      console.log('🔄 🚨 LOADING TEMPLATE DATA FOR THE FIRST AND LAST TIME!');
      loadWorkflowData();
      setHasLoadedData(true);
      setError(null);
    } else if (isOpen && hasLoadedData) {
      console.log('✋ PREVENTED RELOAD - Data already loaded, keeping existing state!');
    }
  }, [isOpen, serviceId, hasLoadedData]);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      setHasLoadedData(false);
    }
  }, [isOpen]);

  const loadWorkflowData = async () => {
    console.log('🚨🚨🚨 loadWorkflowData() CALLED - STACK TRACE:');
    console.trace();
    setLoading(true);
    setError(null);

    try {
      if (isEditing && existingWorkflowData.length > 0) {
        // EDITING MODE: Use existing booking workflow data
        console.log('🔄 Loading existing booking workflow data for editing');
        console.log('📋 Existing workflow data:', existingWorkflowData);
        setBookingWorkflowSteps(existingWorkflowData);
        
        // Get workflow info for display
        const { data: serviceWorkflow } = await supabase
          .from('service_workflows')
          .select('id, name, description')
          .eq('service_id', serviceId)
          .eq('is_active', true)
          .single();
        
        setWorkflow(serviceWorkflow);
      } else {
        // NEW ASSIGNMENT MODE: Load template and create booking-specific data
        console.log('🆕 Loading workflow template to create booking-specific workflow');
        
        // Get the service workflow template
        const { data: serviceWorkflow, error: workflowError } = await supabase
          .from('service_workflows')
          .select('id, name, description')
          .eq('service_id', serviceId)
          .eq('is_active', true)
          .single();

        if (workflowError) throw workflowError;
        setWorkflow(serviceWorkflow);

        // Get the template workflow steps with their IDs for default resource lookup
        const { data: fullTemplateSteps, error: stepsError } = await supabase
          .from('workflow_steps')
          .select('id, title, description, order_index, workflow_step_library_id')
          .eq('workflow_id', serviceWorkflow.id)
          .order('order_index', { ascending: true });

        if (stepsError) throw stepsError;

        // Convert template steps to booking workflow steps - START EMPTY
        const bookingSteps: BookingWorkflowStep[] = fullTemplateSteps.map(templateStep => ({
          workflow_step_library_id: templateStep.workflow_step_library_id || '',
          title: templateStep.title,
          description: templateStep.description,
          order_index: templateStep.order_index,
          assigned_resources: [] // Always start empty
        }));

        // Set the empty steps first
        setBookingWorkflowSteps(bookingSteps);

        // Now load default resources and apply them using frontend logic
        console.log('🔄 Loading default resources from template...');
        
        for (let stepIndex = 0; stepIndex < fullTemplateSteps.length; stepIndex++) {
          const templateStep = fullTemplateSteps[stepIndex];
          
          try {
            const { data: defaultResources, error: resourcesError } = await supabase
              .from('workflow_step_default_resources')
              .select(`
                resource_id,
                resources:resource_id (
                  id,
                  first_name,
                  last_name,
                  email,
                  avatar_url
                )
              `)
              .eq('workflow_step_id', templateStep.id);

            if (resourcesError) {
              console.error('Error loading default resources for step:', templateStep.id, resourcesError);
              continue;
            }

            // Format and apply default resources using frontend logic
            const defaultResourcesList = (defaultResources || [])
              .filter(dr => dr.resources)
              .map(dr => dr.resources as DefaultResource);

            if (defaultResourcesList.length > 0) {
              console.log(`📋 Auto-applying ${defaultResourcesList.length} default resources to step "${templateStep.title}"`);
              
              // Apply each resource using the same frontend logic
              for (const resource of defaultResourcesList) {
                console.log('🔄 Auto-adding:', resource.first_name, resource.last_name);
                await applyResourceToStep(stepIndex, resource);
              }
            }
          } catch (error) {
            console.error('Error processing default resources for step:', templateStep.title, error);
          }
        }
      }
    } catch (err) {
      console.error('Error loading workflow data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load workflow data');
    } finally {
      setLoading(false);
    }
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  const handleClose = () => {
    setIsAnimating(false);
    onClose();
  };

  const handleSkip = () => {
    onSkip();
    handleClose();
  };

  const handleContinue = () => {
    // RESOURCES DISABLED - NO VALIDATION NEEDED
    console.log('✅ Passing booking-specific workflow data to parent:', bookingWorkflowSteps);

    // Pass booking-specific workflow data to parent
    onContinue(bookingWorkflowSteps);
    handleClose();
  };

  // Resource management functions  
  const handleAddResource = (stepIndex: number) => {
    console.log('➕ Adding resource to step index:', stepIndex);
    setCurrentStepIndex(stepIndex);
    setIsSelectResourceModalOpen(true);
  };

  // Helper function to apply a resource to a step (used by both manual selection and template defaults)
  const applyResourceToStep = async (stepIndex: number, resource: any) => {
    return new Promise<void>((resolve) => {
      setBookingWorkflowSteps(prev => {
        const currentStep = prev[stepIndex];
        
        // Check if resource is already assigned to this step
        const isAlreadyAssigned = currentStep.assigned_resources.some(r => r.id === resource.id);
        
        if (isAlreadyAssigned) {
          console.log('⚠️ Resource already assigned to this step, skipping:', resource.first_name, resource.last_name);
          resolve();
          return prev;
        }

        const newResource: DefaultResource = {
          id: resource.id,
          first_name: resource.first_name,
          last_name: resource.last_name,
          email: resource.email,
          avatar_url: resource.avatar_url
        };

        const updated = prev.map((step, index) => 
          index === stepIndex 
            ? { ...step, assigned_resources: [...step.assigned_resources, newResource] }
            : step
        );
        
        resolve();
        return updated;
      });
    });
  };

  // Get assigned resources for the CURRENT step only (each step is independent)
  const getCurrentStepAssignedResources = () => {
    if (currentStepIndex === null) return [];
    
    const currentStep = bookingWorkflowSteps[currentStepIndex];
    return currentStep.assigned_resources.map(resource => ({
      ...resource,
      assignedStep: currentStep.title || `Step ${currentStepIndex + 1}`
    }));
  };

  const handleResourceSelected = async (resource: any) => {
    if (currentStepIndex !== null) {
      const currentStep = bookingWorkflowSteps[currentStepIndex];
      
      // Check if resource is already assigned to this step
      const isAlreadyAssigned = currentStep.assigned_resources.some(r => r.id === resource.id);
      
      if (isAlreadyAssigned) {
        alert('This resource is already assigned to this step.');
        return;
      }

      console.log('➕ Manually adding resource:', resource.first_name, resource.last_name, 'to step:', currentStepIndex);

      // Use the same helper function as template defaults
      await applyResourceToStep(currentStepIndex, resource);
    }
    setIsSelectResourceModalOpen(false);
    setCurrentStepIndex(null);
  };

  const handleRemoveResource = (stepIndex: number, resourceId: string) => {
    console.log('🗑️ Removing resource:', resourceId, 'from step:', stepIndex);
    
    // Simple state update - remove resource from specific step
    setBookingWorkflowSteps(prev => prev.map((step, index) => 
      index === stepIndex 
        ? { ...step, assigned_resources: step.assigned_resources.filter(r => r.id !== resourceId) }
        : step
    ));
  };

  // Delete workflow step (booking-specific only, no database changes!)
  const handleDeleteStep = (stepIndex: number) => {
    const stepToDelete = bookingWorkflowSteps[stepIndex];
    
    console.log(`🗑️ Removing step from booking workflow: "${stepToDelete?.title}"`);
    
    // Remove from booking workflow steps (no database changes!)
    setBookingWorkflowSteps(prev => prev.filter((_, index) => index !== stepIndex));
  };

  // Swap workflow step (booking-specific only!)
  const handleSwapStep = (stepIndex: number) => {
    const step = bookingWorkflowSteps[stepIndex];
    console.log('🔄 Swap button clicked for step:', step.title);
    setCurrentStepToSwap(step);
    setInsertPosition(stepIndex); // Use position for swapping
    setIsWorkflowLibraryModalOpen(true);
  };

  // Insert new step at specific position (booking-specific only!)
  const handleInsertStep = (position: number) => {
    console.log('➕ Insert button clicked at position:', position);
    setInsertPosition(position);
    setCurrentStepToSwap(null); // Clear swap step when inserting
    setIsWorkflowLibraryModalOpen(true);
  };

  // Replace current step with library step OR insert new step (BOOKING-SPECIFIC ONLY!)
  const handleSelectLibraryStep = async (libraryStep: { id: string; title: string; description?: string; category?: string }) => {
    console.log('📚 Selected library step:', libraryStep.title);

    if (currentStepToSwap && insertPosition !== null) {
      // SWAP MODE: Replace existing step in booking workflow
      console.log(`🔄 Swapping booking step: "${currentStepToSwap.title}" → "${libraryStep.title}"`);
      
      setBookingWorkflowSteps(prev => prev.map((step, index) => 
        index === insertPosition 
          ? { 
              ...step, 
              workflow_step_library_id: libraryStep.id,
              title: libraryStep.title, 
              description: libraryStep.description || '',
              // Keep existing assigned resources
            }
          : step
      ));
    } else if (insertPosition !== null) {
      // INSERT MODE: Add new step at specific position in booking workflow
      console.log(`➕ Inserting new step at position ${insertPosition}: "${libraryStep.title}"`);
      
      const newBookingStep: BookingWorkflowStep = {
        workflow_step_library_id: libraryStep.id,
        title: libraryStep.title,
        description: libraryStep.description || '',
        order_index: insertPosition,
        assigned_resources: [] // Start with no resources, user can add them
      };

      setBookingWorkflowSteps(prev => {
        const newSteps = [...prev];
        // Update order_index for steps at and after insert position
        newSteps.forEach(step => {
          if (step.order_index >= insertPosition) {
            step.order_index += 1;
          }
        });
        
        // Insert the new step
        newSteps.splice(insertPosition, 0, newBookingStep);
        
        // Re-index all steps to ensure proper order
        return newSteps.map((step, index) => ({ ...step, order_index: index }));
      });
    }

    // Close the library modal
    setIsWorkflowLibraryModalOpen(false);
    setCurrentStepToSwap(null);
    setInsertPosition(null);
  };

  if (!isOpen && !isAnimating) return null;

  const modalContent = (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[100000]`}
      onClick={onClose}
      style={{
        zIndex: 100000,
        transform: 'translateZ(0)',
        WebkitTransform: 'translateZ(0)',
        isolation: 'isolate',
        touchAction: 'manipulation',
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out shadow-2xl ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="bg-black border-b border-gray-800">
            <div className="flex items-center justify-between px-6 py-4">
              <h2 className="text-xl font-semibold text-white">
                {workflow?.name || 'Workflow Assignment'}
              </h2>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto bg-gray-50">
            <div className="p-6">
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading workflow...</p>
                </div>
              ) : (
                <div>
                  {/* Show error message if there is one, but don't hide the workflow steps */}
                  {error && (
                    <div className="mb-4">
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 relative">
                        <p className="text-red-600 text-sm pr-8">{error}</p>
                        <button
                          onClick={() => setError(null)}
                          className="absolute top-2 right-2 text-red-400 hover:text-red-600 transition-colors"
                          title="Dismiss error"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {bookingWorkflowSteps.length > 0 ? (
                <div>
                  <div className="mb-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Workflow Steps
                    </h3>
                    <p className="text-gray-600 text-sm">
                      This workflow contains {bookingWorkflowSteps.length} step{bookingWorkflowSteps.length !== 1 ? 's' : ''}.
                    </p>
                  </div>

                                     {/* Workflow Steps List */}
                   <div className="space-y-3">
                     {/* Insert button before first step */}
                     {bookingWorkflowSteps.length > 0 && (
                       <div className="flex justify-center">
                         <button
                           onClick={() => handleInsertStep(0)}
                           className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-600 hover:text-blue-700 transition-colors"
                           title="Insert step here"
                         >
                           <Plus className="h-4 w-4" />
                         </button>
                       </div>
                     )}

                     {bookingWorkflowSteps.map((step, index) => (
                       <React.Fragment key={`${step.workflow_step_library_id}-${index}`}>
                         <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm relative">
                                 {/* Action Buttons */}
                                 <div className="absolute top-3 right-3 flex space-x-1">
                                   <button
                                     onClick={() => handleSwapStep(index)}
                                     className="text-gray-400 hover:text-blue-500 transition-colors p-1 hover:bg-blue-50 rounded"
                                     title="Replace with library step"
                                   >
                                     <RefreshCw className="h-4 w-4" />
                                   </button>
                                   <button
                                     onClick={() => handleDeleteStep(index)}
                                     className="text-gray-400 hover:text-red-500 transition-colors p-1 hover:bg-red-50 rounded"
                                     title="Delete workflow step"
                                   >
                                     <Trash2 className="h-4 w-4" />
                                   </button>
                                 </div>

                                 <div className="flex items-start space-x-3 pr-16">
                                 <div className="flex-shrink-0">
                                   <div className="h-8 w-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-medium">
                                     {index + 1}
                                   </div>
                                 </div>
                                                                  <div className="flex-1 min-w-0">
                             <h4 className="text-sm font-medium text-gray-900 mb-1">
                               {step.title}
                             </h4>
                             {step.description && (
                               <p className="text-xs text-gray-600 mb-2">
                                 {step.description}
                               </p>
                             )}
                            
                                                                 {/* Assigned Resources List */}
                                     <div className="mt-3 pt-2 border-t border-gray-100">
                                       <div className="flex items-center justify-between mb-2">
                                         <p className="text-xs font-medium text-gray-700">Assigned Resources:</p>
                                         <button
                                           onClick={() => handleAddResource(index)}
                                           className="flex items-center space-x-1 px-2 py-1 text-xs bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-md transition-colors"
                                         >
                                           <Plus className="h-3 w-3" />
                                           <span>Add</span>
                                         </button>
                                       </div>
                                       
                                       {step.assigned_resources?.length > 0 ? (
                                         <div className="space-y-2">
                                           {step.assigned_resources.map((resource) => (
                                             <div
                                               key={resource.id}
                                               className="flex items-center justify-between px-3 py-2 bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                                             >
                                               <div className="flex items-center space-x-2">
                                                 {/* Resource Avatar */}
                                                 <div className="h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center text-xs font-medium text-blue-600 overflow-hidden">
                                                   {resource.avatar_url ? (
                                                     <img 
                                                       src={resource.avatar_url} 
                                                       alt={`${resource.first_name} ${resource.last_name}`}
                                                       className="h-full w-full object-cover rounded-full"
                                                     />
                                                   ) : (
                                                     `${resource.first_name[0]}${resource.last_name[0]}`
                                                   )}
                                                 </div>
                                                 {/* Resource Name */}
                                                 <span className="text-xs text-gray-900 font-medium">
                                                   {resource.first_name} {resource.last_name}
                                                 </span>
                                               </div>
                                               <button
                                                 onClick={() => handleRemoveResource(index, resource.id)}
                                                 className="text-red-500 hover:text-red-700 transition-colors p-1 hover:bg-red-50 rounded"
                                                 title="Remove resource"
                                               >
                                                 <Trash2 className="h-3 w-3" />
                                               </button>
                                             </div>
                                           ))}
                                         </div>
                                       ) : (
                                         <p className="text-xs text-gray-500 italic">No resources assigned yet</p>
                                       )}
                            </div>
                                                                     </div>
                                         </div>
                                       </div>

                         {/* Insert button after this step */}
                         <div className="flex justify-center">
                           <button
                             onClick={() => handleInsertStep(step.order_index + 1)}
                             className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-600 hover:text-blue-700 transition-colors"
                             title="Insert step here"
                           >
                             <Plus className="h-4 w-4" />
                           </button>
                         </div>
                       </React.Fragment>
                     ))}

                     {/* Insert button for empty workflow */}
                     {bookingWorkflowSteps.length === 0 && (
                       <div className="flex justify-center py-8">
                         <button
                           onClick={() => handleInsertStep(0)}
                           className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-600 hover:text-blue-700 transition-colors"
                           title="Add first step"
                         >
                           <Plus className="h-6 w-6" />
                         </button>
                       </div>
                     )}
                   </div>


                </div>
                  ) : (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                        <ClipboardList className="h-8 w-8 text-gray-400" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        {workflow ? 'All workflow steps have been removed.' : 'No workflow steps found for this service.'}
                      </h3>
                      {workflow && (
                        <p className="text-sm text-gray-600">
                          You can skip assignment or add new steps to this workflow template.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Footer with Skip/Continue buttons */}
          <div className="bg-white border-t border-gray-200 p-6">
            <div className="flex gap-3">
              <button
                onClick={handleSkip}
                className="flex-1 flex items-center justify-center px-4 py-3 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Skip Assignment
              </button>
              <button
                onClick={handleContinue}
                className="flex-1 flex items-center justify-center px-4 py-3 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm"
              >
                Continue with Booking
              </button>
            </div>
          </div>
        </div>
      </div>

             {/* Workflow Library Modal */}
       {isWorkflowLibraryModalOpen && (
         <WorkflowStepLibraryModal
           isOpen={isWorkflowLibraryModalOpen}
           onClose={() => {
             console.log('📚 WorkflowStepLibraryModal closing');
             setIsWorkflowLibraryModalOpen(false);
             setCurrentStepToSwap(null);
             setInsertPosition(null);
           }}
           onSelectStep={handleSelectLibraryStep}
         />
       )}

       {/* Select Resource Modal */}
       <SelectResourceModal
         isOpen={isSelectResourceModalOpen}
         onClose={() => {
           setIsSelectResourceModalOpen(false);
           setCurrentStepIndex(null);
         }}
         onSelect={handleResourceSelected}
         selectedResources={getCurrentStepAssignedResources()}
       />
     </div>
   );

   return createPortal(modalContent, document.body);
 }
